import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  CatalogEntityConfig,
  CatalogThemeVariables,
  DomainCatalogConfig,
  DomainCatalogViewModel,
  getPendingCatalogChangeCount,
} from '@shared-lib/models/domain-catalog.models';
import { DomainCatalogDataService } from '@shared-lib/shared-services/domain-catalog-data.service';
import { ThemeService } from '@shared-lib/shared-services/theme.service';
import { Observable, Subscription } from 'rxjs';
import { CatalogFilterPanelComponent } from '../catalog-filter-panel/catalog-filter-panel.component';
import { CatalogPageHeaderComponent } from '../catalog-page-header/catalog-page-header.component';
import { CatalogTableComponent } from '../catalog-table/catalog-table.component';

@Component({
  selector: 'app-domain-catalog-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CatalogPageHeaderComponent,
    CatalogFilterPanelComponent,
    CatalogTableComponent,
  ],
  templateUrl: './domain-catalog-detail.component.html',
  styleUrl: './domain-catalog-detail.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class DomainCatalogDetailComponent implements OnInit, OnDestroy {
  private hasLoadedFromInput = false;
  @Input() set data(value: any) {
    this._data = value;
    this.tryLoadFromData();
  }
  get data(): any {
    return this._data;
  }
  readonly vm$: Observable<DomainCatalogViewModel>;
  private readonly subs = new Subscription();
  private _data: any;
  themeMode: 'light' | 'dark' = 'light';
  private catalogThemeOverrides: CatalogThemeVariables = {};
  @ViewChild('deleteCancelBtn') private deleteCancelBtn?: ElementRef<HTMLButtonElement>;
  private deleteConfirmWasOpen = false;

  constructor(
    public readonly catalogs: DomainCatalogDataService,
    private readonly themeService: ThemeService,
  ) {
    this.vm$ = this.catalogs.vm$;
  }

  ngOnInit(): void {
    this.tryLoadFromData();

    this.subs.add(
      this.themeService.theme$.subscribe((theme) => {
        this.themeMode = this.isDarkTheme(theme) ? 'dark' : 'light';
        this.catalogThemeOverrides = this.buildCatalogThemeOverrides(theme);
      }),
    );

    // Move focus onto the Cancel button as soon as the delete confirmation
    // popup opens, so keyboard users land somewhere sensible inside it.
    this.subs.add(
      this.vm$.subscribe((vm) => {
        if (vm.showDeleteConfirmModal && !this.deleteConfirmWasOpen) {
          setTimeout(() => this.deleteCancelBtn?.nativeElement.focus());
        }
        this.deleteConfirmWasOpen = vm.showDeleteConfirmModal;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /** Warn before a refresh/close discards unsaved local changes on any catalog. */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.catalogs.hasPendingChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  /** Escape closes the delete confirmation popup, matching standard dialog behavior. */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.catalogs.cancelDeleteConfirm();
  }

  getThemeStyles(config: DomainCatalogConfig | null): CatalogThemeVariables {
    return {
      ...this.getCatalogConfigThemeStyles(config),
      ...this.catalogThemeOverrides,
    };
  }

  private getCatalogConfigThemeStyles(config: DomainCatalogConfig | null): CatalogThemeVariables {
    const styles = { ...(config?.theme?.[this.themeMode] || {}) };
    delete styles['--primary'];
    delete styles['--primary-end'];
    delete styles['--primary-rgb'];
    delete styles['--primary-bg'];
    return styles;
  }

  private buildCatalogThemeOverrides(theme: any): CatalogThemeVariables {
    if (!theme) return {};
    const primary = theme.button?.primaryBg || theme.layout?.sidebar?.bg || '#043D63';
    const primaryEnd = theme.button?.primaryHoverBg || primary;
    const primaryRgb = this.hexToRgb(primary) || '4, 61, 99';

    return {
      '--primary': primary,
      '--primary-end': primaryEnd,
      '--primary-rgb': primaryRgb,
      '--primary-bg': `rgba(${primaryRgb}, ${this.themeMode === 'dark' ? '0.16' : '0.08'})`,
      '--bg-app': theme.layout?.content?.bg || 'var(--dvm-content-bg, #d9dbdd)',
      '--text-main': theme.layout?.content?.text || 'var(--dvm-content-text, #202124)',
      '--text-muted': theme.chart?.axisLabelColor || 'var(--dvm-form-placeholder-color, #5f6368)',
    };
  }

  private hexToRgb(hex: string): string | null {
    const normalized = hex.replace('#', '').trim();
    if (!/^[0-9a-f]{6}$/i.test(normalized)) return null;
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    return `${r}, ${g}, ${b}`;
  }

  private isDarkTheme(theme: any): boolean {
    const bg = String(theme?.layout?.content?.bg || '').replace('#', '').slice(0, 6);
    if (!/^[0-9a-f]{6}$/i.test(bg)) return this.themeService.currentThemeName === 'dark';
    const r = parseInt(bg.slice(0, 2), 16);
    const g = parseInt(bg.slice(2, 4), 16);
    const b = parseInt(bg.slice(4, 6), 16);
    return r * 0.299 + g * 0.587 + b * 0.114 < 128;
  }

  /**
   * Number of unsaved changes to surface on the Save button. Mirrors what
   * `saveAll` actually persists. Each affected row is counted once, including
   * edits, inserts, active toggles, and deletes.
   */
  getPendingChangeCount(vm: DomainCatalogViewModel): number {
    return getPendingCatalogChangeCount(vm);
  }

  get detailComponentConfig(): any {
    return this.data?.detailComponent || {};
  }

  get addCatalogModalConfig(): any {
    return this.data?.addCatalogModal || {};
  }

  getHeaderConfig(activeConfig: any): any {
    return this.mergeUiConfig(this.detailComponentConfig?.header, activeConfig?.pageHeader);
  }

  getFilterPanelConfig(activeConfig: any): any {
    return this.mergeUiConfig(this.detailComponentConfig?.filterPanel, activeConfig?.filterPanel);
  }

  getTableConfig(activeConfig: any): any {
    return this.mergeUiConfig(this.detailComponentConfig?.table, activeConfig?.table);
  }

  resolveActiveConfig(vm: DomainCatalogViewModel): CatalogEntityConfig | null {
    if (this.catalogs.activeConfig) {
      return this.catalogs.activeConfig;
    }

    const config = vm.config;
    if (!config?.entities?.length) {
      return null;
    }

    return config.entities.find((entity) => entity.id === vm.activeCatalogId) || config.entities[0] || null;
  }

  showDetailSection(sectionKey: 'header' | 'filterPanel' | 'table', activeConfig: any): boolean {
    const config = sectionKey === 'header'
      ? this.getHeaderConfig(activeConfig)
      : sectionKey === 'filterPanel'
        ? this.getFilterPanelConfig(activeConfig)
        : this.getTableConfig(activeConfig);
    return config?.visible !== false;
  }

  private mergeUiConfig(baseConfig: any, overrideConfig: any): any {
    return {
      ...(baseConfig || {}),
      ...(overrideConfig || {}),
    };
  }

  private tryLoadFromData(): void {
    if (!this.data || this.hasLoadedFromInput) {
      return;
    }

    this.hasLoadedFromInput = true;
    this.catalogs.load(
      this.data?.configUrl || this.data?.apiUrl || 'assets/dashboard-config/domain-catalogs.json',
      this.data?.dataApiUrl,
    );
  }

}

